import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {SortDirection} from '@angular/material/sort';
import {forkJoin, Observable, of, Subscriber, throwError, timer} from 'rxjs';
import {catchError, delayWhen, first, map, switchMap, tap} from 'rxjs/operators';
import {
    ReportResponseObj,
    ReportRunParamsObj,
    ReportSearchResultObj,
    ReportUiDataObj, ReportUserConfigurationObj,
    ReportViewObj
} from '../../../../generated/generated-core.model';
import {DISPATCHER_PREFIX, MS_REQUEST_TIMEOUT} from '../../../consts/data-service-consts';
import {
    MX_AUTHENTICATION_SERVICE_TOKEN,
    MxAuthenticationService
} from '../../../shared/services/mx-authentication.service';
import {MxDownloadService} from '../../../shared/services/mx-download.service';
import {MxNotificationService} from '../../../shared/services/mx-notification.service';


const CORE_REPORTS_REST_URL: string = DISPATCHER_PREFIX + '/core-reports';
const REPORTS_REST_URL: string = DISPATCHER_PREFIX + '/reports';
const REPORT_JOBS_REST_URL: string = DISPATCHER_PREFIX + '/report-jobs';
const REPORT_USER_CONFIG_REST_URL: string = DISPATCHER_PREFIX + '/report-user-configs';
export const EMPTY_VALUE_CONST: string = 'EMPTY_VALUE';

@Injectable()
export class MxReportsDataService {
    public runningJobId: number = null;

    constructor(
        private downloadService: MxDownloadService,
        private httpClient: HttpClient,
        private notificationService: MxNotificationService,
        @Inject(MX_AUTHENTICATION_SERVICE_TOKEN) private authService: MxAuthenticationService
    ) {}

    getFilterParamsFromPayload(formValue: Array<any>): Array<{field: string, value?: any}> {
        let filterParams: Array<{field: string, value?: any}> = [];

        for (let filterName in formValue) {
            if (formValue[filterName] !== '' && formValue[filterName] !== EMPTY_VALUE_CONST) {
                filterParams.push({
                    field: filterName,
                    value: formValue[filterName]
                });
            } else if (formValue[filterName] === EMPTY_VALUE_CONST) {
                filterParams.push({
                    field: filterName
                });
            }
        }
        return filterParams;
    }

    getReportView(id: string, name: string, runParams: ReportRunParamsObj): Observable<ReportResponseObj> {
        return this.getReport(name, id, runParams).pipe(first());
    }

    getReportResults(pageName: string, filterParams: Array<{field: string, value?: any}>, id: number, column: string,
                     direction: SortDirection, runParams: ReportRunParamsObj): Observable<ReportSearchResultObj> {
        if (!id) {
            return this.getCoreReport(pageName, filterParams, column, direction ===  'asc');
        } else {
            return this.getInnerReport(id, filterParams, runParams);
        }
    }

    getCoreReport(pageName: string, filterParams: Array<any>, column: string, ascending: boolean): Observable<ReportSearchResultObj> {
        const url: string = CORE_REPORTS_REST_URL + `/results?sortBy=${column}&ascending=${ascending}`;
        let payLoad = {reportName: pageName, filters: filterParams};
        return this.httpClient.post<ReportSearchResultObj>(url, payLoad);
    }

    getInnerReport( id: number, filterParams: Array<any>, runParams: ReportRunParamsObj): Observable<ReportSearchResultObj> {
        const runReportUrl: string = REPORTS_REST_URL + '/run-inner-report';
        let payLoad = {reportId: id, filters: filterParams, runParams};
        return this.httpClient.post<number>(runReportUrl, payLoad)
            .pipe(
                switchMap((response: number) => this.getReportResultsByJobId(response))
            );
    }

    getReportResultsByJobId(jobId: number): Observable<ReportSearchResultObj> {
        if (this.runningJobId === -1) { // if report was canceled before job have been started we terminate getting real report results
            this.notificationService.showSuccess('Your action was canceled.');
            this.runningJobId = null;
            return of(null);
        }

        const jobStatusUrl: string = REPORT_JOBS_REST_URL + `/${jobId}/status`;
        this.runningJobId = jobId;
        return this.httpClient.get(jobStatusUrl, {responseType: 'text'})
            .pipe(
                switchMap((response) => {
                    let status = response.toLowerCase();

                    if (status === 'cancelled' || status === 'unknown') {
                        this.runningJobId = null;
                        return of(null);
                    }

                    if (status === 'finished') {
                        if (this.runningJobId === null) {
                            return of(null);
                        }
                        this.runningJobId = null;
                        const innerResultsUrl: string = REPORT_JOBS_REST_URL + `/${jobId}/inner-results`;
                        return this.httpClient.get<ReportSearchResultObj>(innerResultsUrl);
                    }

                    if (status === 'running' || status === 'initial') {
                        return of(null).pipe(
                            delayWhen(() => timer(MS_REQUEST_TIMEOUT)),
                            switchMap(() => this.getReportResultsByJobId(jobId))
                        );
                    }

                    if (status === 'error') {
                        return throwError('Error while running sql statement');
                    } else {
                        return throwError(response);
                    }
                })
            );
    }

    getExportedReport(formValue: any, id: number, name: string,
                      outputColumns: ReadonlyArray<string>, reportRunParams: ReportRunParamsObj): Observable<void> {
        let filterParams = this.getFilterParamsFromPayload(formValue);
        let payLoad: {
            reportName: string;
            outputColumns: ReadonlyArray<string>;
            filters: Array<{field: string; value?: any}>
            runParams?: ReportRunParamsObj
        } | {
            reportId: number;
            outputColumns: ReadonlyArray<string>;
            filters: Array<{field: string; value?: any}>
            runParams?: ReportRunParamsObj
        };
        let runReportUrl: string;
        if (!id) {
            runReportUrl = CORE_REPORTS_REST_URL + '/run-core-report-for-export';
            payLoad = {
                reportName: name,
                outputColumns,
                filters: filterParams
            };
        } else {
            runReportUrl = REPORTS_REST_URL + '/run-inner-report-for-export';
            payLoad = {
                reportId: id,
                outputColumns,
                filters: filterParams,
                runParams: reportRunParams
            };
        }

        return this.httpClient.post<number>(runReportUrl, payLoad)
            .pipe(
                switchMap(jobId => this.getExportedReportByJobId(jobId))
            );
    }

    getExportedReportByJobId(jobId: number): Observable<void> {
        if (this.runningJobId === -1) { // if report was canceled before job have been started we terminate getting real report results
            this.notificationService.showSuccess('Your action was canceled.');
            this.runningJobId = null;
            return of(null);
        }

        const jobStatusUrl: string = REPORT_JOBS_REST_URL + `/${jobId}/status`;
        this.runningJobId = jobId;
        return this.httpClient.get(jobStatusUrl, {responseType: 'text'})
            .pipe(
                switchMap((response) => {
                    let status = response.toLowerCase();

                    if (status === 'cancelled' || status === 'unknown') {
                        this.runningJobId = null;
                        return of(null);
                    }

                    if (status === 'finished') {
                        if (this.runningJobId === null) {
                            return of(null);
                        }
                        this.runningJobId = null;
                        const exportedReportUrl: string = REPORT_JOBS_REST_URL
                            + `/${jobId}/export-results?X-Auth-Token=` + this.authService.token;
                        return of(null)
                            .pipe(
                                switchMap(() => this.httpClient.get(exportedReportUrl, {responseType: 'blob', observe: 'response'})
                                    .pipe(
                                        switchMap((value) => this.downloadService.saveResponseAsFile(value)),
                                        map(()=> null)
                                    )
                                )
                            );
                    }

                    if (status === 'running' || status === 'initial') {
                        return of(null).pipe(
                            delayWhen(() => timer(MS_REQUEST_TIMEOUT)),
                            switchMap(() => this.getExportedReportByJobId(jobId))
                        );
                    }

                    if (status === 'error') {
                        return throwError('Error while running sql statement');
                    } else {
                        return throwError(response);
                    }
                })
            );
    }

    getReport(reportName: string, reportDefId: string, runParams: ReportRunParamsObj): Observable<ReportResponseObj> {
        let reportUrl: string;
        let params;
        if (reportName) {
            reportUrl = CORE_REPORTS_REST_URL + `/report`;
            params = { reportName};
        } else {
            reportUrl = REPORTS_REST_URL + `/inner-report`;
            params = { reportDefId};
            if (runParams && runParams.affiliateId) {
                params = {...params, affiliateId: runParams.affiliateId};
            }
            if (runParams && runParams.campaignId) {
                params = {...params, campaignId: runParams.campaignId};
            }
            if (runParams && runParams.asAffiliate) {
                params = {...params, runAsAffiliate: runParams.asAffiliate};
            }
        }
        return this.httpClient.get<ReportResponseObj>(reportUrl, {params})
            .pipe(
                switchMap(reportResponseObj => this.updateReferencedReportInputs(reportResponseObj))
            );
    }

    getReportInput(pageName: string, id?: number): Observable<Array<ReportUiDataObj>> {
        let reportInputUrl: string;
        if (!id) {
            reportInputUrl = CORE_REPORTS_REST_URL + `/input?reportName=${pageName}`;
        } else {
            reportInputUrl = REPORTS_REST_URL + `/inner-input?reportDefId=${id}`;
        }
        return this.httpClient.get<Array<ReportUiDataObj>>(reportInputUrl);
    }

    getReportOutput(pageName: string, id: number): Observable<Array<ReportUiDataObj>> {
        let reportOutputUrl: string;
        if (!id) {
            reportOutputUrl = CORE_REPORTS_REST_URL + `/output?reportName=${pageName}`;
        } else {
            // Assuming, that we don't need this for /output, as we don't seem to have referencedReports there.
            reportOutputUrl = REPORTS_REST_URL + `/inner-output?reportDefId=${id}`;
        }
        return this.httpClient.get<Array<ReportUiDataObj>>(reportOutputUrl);
        // TODO: Do we need to obtain Observable errors somehow?
    }

    updateReferencedReportInputs(reportResponseObj: ReportResponseObj): Observable<ReportResponseObj> {
        let referencedIdsToOutputs: Map<number, Array<any>> = new Map();
        // phase 1 - group by. referencedReportId => list of outputColumn, which reference it
        for (let i = 0; i < reportResponseObj.output.length; i++) {
            let output = reportResponseObj.output[i];
            if (output.referencedReport && output.referencedReport.reportId) {
                let referencedReportId = output.referencedReport.reportId;
                if (!referencedIdsToOutputs.has(referencedReportId)) {
                    referencedIdsToOutputs.set(referencedReportId, []);
                }
                referencedIdsToOutputs.get(referencedReportId).push(output);
            }
        }
        let observables: Observable<Array<ReportUiDataObj>>[] = [];
        referencedIdsToOutputs.forEach((outputs, referencedId) => {
            observables.push(this.getReportInput(reportResponseObj.name, referencedId)
                .pipe(
                    tap((input) => {
                            outputs.forEach((output) => {
                                output.referencedReportInput = input;
                            });
                        }
                    ),
                    catchError(err => {
                        console.debug(err.message);
                        return of([new ReportUiDataObj()]);
                    })
                )
            );
        });
        if (observables.length > 0) {
            return forkJoin(observables).pipe(
                switchMap(() => of(reportResponseObj))
            );
        } else {
            return of(reportResponseObj);
        }
    }

    cancelRunningJob(): Observable<string> {
        let runningJobId = this.runningJobId;
        this.runningJobId = null;
        const cancelJobUrl: string = REPORT_JOBS_REST_URL + `/${runningJobId}/cancel`;
        return this.httpClient.post(cancelJobUrl, {}, {responseType: 'text'});
    }

    cancelRunningJobSynchronous(): string {
        let runningJobId = this.runningJobId;
        if (!runningJobId) {
            return 'UNKNOWN';
        }
        this.runningJobId = null;
        const cancelJobUrl: string = REPORT_JOBS_REST_URL + `/${runningJobId}/cancel`;
        let xmlHttp = new XMLHttpRequest();
        xmlHttp.open('POST', cancelJobUrl, false);
        xmlHttp.send(null);
        return xmlHttp.responseText;
    }

    getReportSql(reportId: number, filters: any, runParams: ReportRunParamsObj): void {

        let getSqlUrl: string = REPORTS_REST_URL + `/${reportId}/sql`;
        let payLoad = {reportId, filters, runParams};
        this.httpClient.post(getSqlUrl, payLoad, {
            responseType : 'blob',
            observe: 'response'
        }).subscribe( (response: HttpResponse<Blob>) => {
            if (response.status === 200) {
                this.downloadService.saveResponseAsFile(response);
            } else {
                this.notificationService.showFailure('Problem with saving sql');
            }
        });
    }

    getReportByDefId(defId: number): Observable<ReportViewObj> {
        return this.httpClient.get<ReportViewObj>(REPORTS_REST_URL + `/by-def-id?reportDefId=${defId}`);
    }

    getReportXml(id: number, runParams: ReportRunParamsObj): Observable<Observable<HttpResponse<Blob>>> {
        let getXmlUrl: string = REPORTS_REST_URL + `/${id}/xml`;
        let getXml$ = this.httpClient.post(getXmlUrl, runParams,{
            responseType : 'blob',
            observe: 'response'
        });
        return new Observable((result: Subscriber<Observable<HttpResponse<Blob>>>) => {
            getXml$.subscribe( (response: HttpResponse<Blob>) => {
                if (response.status === 200) {
                    this.downloadService.saveResponseAsFile(response);
                } else {
                    this.notificationService.showFailure('Problem with saving xml');
                }
            });
        });
    }

    saveUserConfig(config: ReportUserConfigurationObj) {
        this.httpClient.post<ReportUserConfigurationObj>(REPORT_USER_CONFIG_REST_URL, config)
            .subscribe();
    }

    getUserConfigs(reportName: string): Observable<Array<ReportUserConfigurationObj>> {
        return this.httpClient.get<Array<ReportUserConfigurationObj>>(`${REPORT_USER_CONFIG_REST_URL}/${reportName}`);
    }

    deleteUserConfigs(reportName: string, configName: string): void {
        this.httpClient.delete(REPORT_USER_CONFIG_REST_URL,
            {params: {reportName, configName}})
            .subscribe();
    }
}
